import { create } from 'zustand'
import { supabase } from '@/integrations/supabase/client'
import type { BikeType, RoadRecord, RoadsMeta } from './types'
import { fetchRoads as apiFetchRoads, fetchRoadsVersionMeta, getRegions } from './api'
import type { RoadTrim, RouteTrims } from './trim'

export const ALL_BIKE_TYPES: BikeType[] = ['Sport', 'Cruiser', 'ADV', 'Sumo']

// ── Duplicate-pass trim keys ────────────────────────────────────────────────
// A road can appear in a route multiple times, each visit trimmed differently.
// Trim keys identify the OCCURRENCE: the 1st pass uses the plain road id (so
// every already-saved route keeps working), later passes use "id#2", "id#3"…
// and always seed an empty trim object so the tray can show a chip per visit.
export function occurrenceKey(roadId: string, occurrence: number): string {
  return occurrence === 0 ? roadId : `${roadId}#${occurrence + 1}`
}

// Occurrence number of the entry at the given route index.
export function occurrenceOf(routeIds: string[], index: number): number {
  const id = routeIds[index]
  let n = 0
  for (let i = 0; i < index; i++) if (routeIds[i] === id) n++
  return n
}

// Key under which the entry at the given route index stores its trim.
export function trimKeyFor(routeIds: string[], index: number): string {
  return occurrenceKey(routeIds[index] ?? '', occurrenceOf(routeIds, index))
}

export interface RoadFilters {
  bikeTypes: BikeType[]
  minOverall: number
  minFun: number
  minPavement: number
  minTraffic: number
  minDirt: number
  minLength: number
  maxLength: number
  fullyPavedOnly: boolean
  highwaysOnly: boolean
  showRated: boolean
  showUnrated: boolean
  search: string
  region: string | null
}

export const DEFAULT_FILTERS: RoadFilters = {
  bikeTypes: [],
  minOverall: 0,
  minFun: 0,
  minPavement: 0,
  minTraffic: 0,
  minDirt: 0,
  minLength: 0,
  maxLength: 1000000,
  fullyPavedOnly: false,
  highwaysOnly: false,
  showRated: true,
  showUnrated: true,
  search: '',
  region: null,
}

export interface Ride {
  mode: 'road' | 'route'
  roadIds: string[]
}

interface RoadState {
  roads: RoadRecord[]
  meta: RoadsMeta | null
  loading: boolean
  error: string | null
  fetchRoads: (force?: boolean) => Promise<void>
  initRoads: () => void
  disposeRoads: () => void
  replaceRoad: (road: RoadRecord) => void
  // Moderator-curated exclusion list: hidden roads are stripped from the
  // visible road list (map, search, filter counts) until restored.
  hiddenRoadIds: Set<string>
  loadHiddenRoads: () => Promise<void>
  // After a moderator hides a road, drop it from the live list immediately
  // (the next full fetch would also exclude it via hiddenRoadIds).
  hideRoadLocally: (roadId: string) => void
  filters: RoadFilters
  setFilters: (patch: Partial<RoadFilters>) => void
  resetFilters: () => void
  selectedId: string | null
  // When the selection came from a route-tray chip of a road visited several
  // times, this is that visit's trim key ("id" / "id#2"…) — the camera uses
  // it to frame that visit's own trimmed span. Null for map/search picks.
  selectedVisitKey: string | null
  selectRoad: (id: string | null, visitKey?: string | null) => void
  // One-call "make room" signal: bumps a counter the page watches to close
  // floating panels (filters, find-a-ride) AND deselects the selected road.
  dismissOverlaysSeq: number
  dismissOverlays: () => void
  // Sign-out reset: clear the selected road, route and ride, and bump the
  // dismiss signal so every floating panel closes back to the bare map.
  resetForSignOut: () => void
  // Bumped whenever the user taps anywhere on the map: the mobile search bar
  // (and its results list) should close, per requested UX.
  searchDismissSeq: number
  dismissSearch: () => void
  // Bumped on every map tap: floating panels that live outside the map
  // (account dropdown, etc.) watch this to close when the user clicks away
  // onto the map. Kept separate from dismissOverlaysSeq so a tap on a ROAD
  // still selects it while the panels still close.
  mapClickSeq: number
  bumpMapClick: () => void
  // Road currently being force-recalculated — the map hides its polyline
  // while set, and the road reappears when the fresh geometry resolves.
  recalculatingId: string | null
  setRecalculating: (id: string | null) => void
  // Force Road Recalc handshake. The PANEL sets recalcWatchId right after the
  // server rebuilt the road; the MAP sets recalcDrawnId when that road's
  // polyline has been resolved AND drawn back onto the map. The success popup
  // fires only on recalcDrawnId — never before the road is actually visible.
  recalcWatchId: string | null
  setRecalcWatchId: (id: string | null) => void
  recalcDrawnId: string | null
  setRecalcDrawn: (id: string | null) => void
  hoveredId: string | null
  setHoveredId: (id: string | null) => void
  // The road chip hovered in the route tray, as a VISIT key ("id" or "id#2")
  // so duplicate visits of the same road can be hovered individually.
  routeHoverKey: string | null
  setRouteHoverKey: (id: string | null) => void
  routeIds: string[]
  setRouteIds: (ids: string[]) => void
  // Display name of the current route (set when it's saved/shared, or when a
  // named saved route is loaded). Any route edit clears it — the name belongs
  // to that exact road sequence.
  routeName: string | null
  setRouteName: (name: string | null) => void
  // Username of whoever saved/named the currently loaded route — shown as
  // "by X" in the tray; clicking it opens that user's stats panel.
  routeSavedBy: string | null
  setRouteSavedBy: (username: string | null) => void
  // Username whose stats panel is open (null = closed).
  statsUsername: string | null
  openStats: (username: string) => void
  closeStats: () => void
  addToRoute: (id: string) => void
  reorderRoute: (fromIndex: number, toIndex: number) => void
  removeFromRoute: (index: number) => void
  clearRoute: () => void
  focusRoute: () => void
  // Road trimming: which road is in trim mode (handles visible on the map),
  // the committed trims per road id, and the live drag state for previews.
  trimmingRoadId: string | null
  setTrimmingRoad: (id: string | null) => void
  routeTrims: RouteTrims
  setTrim: (roadId: string, trim: RoadTrim | null) => void
  clearTrim: (roadId: string) => void
  // Live drag positions (0..1 along the road) shown on the map handles while
  // dragging — null when no drag is in progress. snapCoord is set when the
  // handle magnetized to a junction with another route road.
  trimDrag: {
    roadId: string
    headPos: number | null
    tailPos: number | null
    snapCoord?: [number, number] | null
    snapRoadId?: string | null
  } | null
  setTrimDrag: (
    d: {
      roadId: string
      headPos: number | null
      tailPos: number | null
      snapCoord?: [number, number] | null
      snapRoadId?: string | null
    } | null,
  ) => void
  ride: Ride | null
  startRide: (mode: 'road' | 'route', roadIds: string[]) => void
  exitRide: () => void
  routeFocusSeq: number
}

let refreshTimer: ReturnType<typeof setInterval> | null = null
const REFRESH_MS = 5 * 60 * 1000
let lastFetchMs = 0
// Store generation: bumped on every initRoads/disposeRoads. In-flight
// requests capture the value at start and drop their results when it has
// moved on — nothing from an old cycle may touch a disposed or freshly
// re-initialized store.
let storeEpoch = 0

// ─── Recurring refresh suppression ────────────────────────────────────────
// The 5-minute timer only fires while the tab is VISIBLE and the browser is
// ONLINE: a background tab or an offline device must not keep hitting the
// server (or wake the database) with nothing to show for it. When the tab
// becomes visible / the connection returns, one refresh runs immediately IF
// the last successful refresh is overdue — otherwise the next tick handles it.
let inFlight = false
let visible = typeof document === 'undefined' || document.visibilityState === 'visible'
let online = typeof navigator === 'undefined' || navigator.onLine !== false
let visibilityListener: (() => void) | null = null
let onlineListener: (() => void) | null = null
let offlineListener: (() => void) | null = null

function canPoll(): boolean {
  return visible && online
}

function handleWake(): () => void {
  return () => {
    const nowVisible = document.visibilityState === 'visible'
    const wasAsleep = !visible || !online
    visible = nowVisible
    online = navigator.onLine !== false
    // Wake-up moment: refresh once if the last success is overdue. A failed
    // refresh leaves lastFetchMs untouched, so the tab retries on the next
    // tick while cached roads stay on screen.
    if (wasAsleep && nowVisible && navigator.onLine !== false) {
      if (Date.now() - lastFetchMs >= REFRESH_MS) void useRoadStore.getState().fetchRoads()
    }
  }
}

export const useRoadStore = create<RoadState>((set, get) => ({
  roads: [],
  meta: null,
  loading: false,
  error: null,

  fetchRoads: async (force?: boolean) => {
    // Background refreshes (every 5 min) must not flash the loading state or
    // trigger a full map rebuild when the sheet data hasn't changed. Overlap
    // guard: a slow refresh simply skips this tick — no duplicate requests.
    const isBackground = get().roads.length > 0
    // Generation guard: a request started under one init/dispose cycle must
    // never apply its results to a disposed or re-initialized store (React
    // StrictMode's setup → cleanup → setup included).
    const epoch = storeEpoch
    const stale = () => epoch !== storeEpoch
    if (inFlight) return
    // In-flight covers the probe AND the fetch (reset in the finally below):
    // a second tick or wake-up arriving mid-probe cannot start an
    // overlapping request.
    inFlight = true
    try {
    if (isBackground && !force) {
      // Skip entirely if a recent refresh already succeeded (< 60s ago).
      if (Date.now() - lastFetchMs < 60_000) return
    }
    if (isBackground && !force) {
      // Freshness probe first: ask the backend for its committed signature
      // and revision only (a few bytes, no roads payload). Skip the full
      // fetch only when the committed version we already show is confirmed
      // unchanged — revision equality when available (catches non-sheet
      // commits whose signature stays the same), otherwise signature
      // equality. Unknown values never skip; we then fetch fully, as before.
      const committed = await fetchRoadsVersionMeta()
      if (stale()) return
      const shown = get().meta
      if (
        committed.version_sig != null &&
        shown?.version_sig != null &&
        committed.version_sig === shown.version_sig
      ) {
        const revKnown = committed.sync_revision != null && shown.sync_revision != null
        if (!revKnown || committed.sync_revision === shown.sync_revision) {
          lastFetchMs = Date.now()
          return
        }
      }
    }
      if (!isBackground) set({ loading: true, error: null })
      const { roads: fetched, meta } = await apiFetchRoads()
      if (stale()) return
      // Moderator-hidden roads are stripped here, at the source — every
      // consumer of s.roads (map, search, filter counts) then excludes them.
      const hidden = get().hiddenRoadIds
      const roads = hidden.size === 0 ? fetched : fetched.filter((r) => !hidden.has(r.id))
      const prev = get().roads
      // Committed-version comparison: the database revision (a decimal
      // string, compared as a string — never via Number) changes on EVERY
      // commit, sheet syncs and non-sheet writes (admin resync, force-reset)
      // alike, so an unchanged content signature alone can never suppress a
      // real update. When the revision is unavailable (older backend, sheet
      // fallback), the content signature plays that role; without either,
      // the per-record comparison below decides. Identical version = do not
      // replace the road array or notify subscribers (no needless rebuilds).
      const sig = meta?.version_sig
      const prevSig = get().meta?.version_sig
      const rev = meta?.sync_revision
      const prevRev = get().meta?.sync_revision
      const revChanged = rev != null && prevRev != null && rev !== prevRev
      const signatureUnchanged = !revChanged && sig != null && prevSig != null && sig === prevSig
      const changed =
        !signatureUnchanged &&
        (roads.length !== prev.length ||
          roads.some((r, i) => !prev[i] || roadSignature(r) !== roadSignature(prev[i])))
      lastFetchMs = Date.now()
      // A revision bump without a road-content change (e.g. a non-sheet
      // commit elsewhere) must still store the new meta — otherwise the
      // next probe would compare against a stale revision and never skip.
      if (changed) set({ roads, meta, loading: false, error: null })
      else if (revChanged || signatureUnchanged) set({ meta, loading: false, error: null })
      else set({ loading: false, error: null })
    } catch (err) {
      if (stale()) return
      const message = err instanceof Error ? err.message : 'Failed to load roads'
      if (!isBackground) set({ loading: false, error: message })
      // Failure path: cached roads are NOT cleared — the map keeps showing
      // the last good data; lastFetchMs is untouched so the next tick retries.
    } finally {
      inFlight = false
    }
  },

  initRoads: () => {
    if (refreshTimer) return // already initialized
    // New generation: requests still in flight from a previous init/dispose
    // cycle must not apply their results to this one.
    storeEpoch++
    // Load the hidden-road exclusion list BEFORE the first fetch so hidden
    // roads never flash onto the map on a cold start.
    void get()
      .loadHiddenRoads()
      .then(() => get().fetchRoads())
    refreshTimer = setInterval(() => {
      // Poll only while someone is looking and the browser is online.
      if (!canPoll()) return
      void get().fetchRoads()
    }, REFRESH_MS)
    // Wake-up refreshes: returning to the tab (or regaining the connection)
    // triggers one immediate refresh when the last success is overdue.
    visibilityListener = handleWake()
    onlineListener = handleWake()
    offlineListener = handleWake()
    document.addEventListener('visibilitychange', visibilityListener)
    window.addEventListener('online', onlineListener)
    window.addEventListener('offline', offlineListener)
  },

  // Removes the recurring timer and the visibility/online listeners. Called
  // when the map page unmounts so nothing keeps polling after it is gone.
  disposeRoads: () => {
    // Invalidate in-flight requests immediately: nothing started under this
    // generation may touch the store after disposal.
    storeEpoch++
    if (refreshTimer) {
      clearInterval(refreshTimer)
      refreshTimer = null
    }
    if (visibilityListener) document.removeEventListener('visibilitychange', visibilityListener)
    if (onlineListener) window.removeEventListener('online', onlineListener)
    if (offlineListener) window.removeEventListener('offline', offlineListener)
    visibilityListener = null
    onlineListener = null
    offlineListener = null
  },

  hiddenRoadIds: new Set<string>(),
  loadHiddenRoads: async () => {
    // RLS: anonymous clients read the exclusion list; failures leave the map
    // unchanged rather than hiding everything.
    const { data, error } = await supabase.from('hidden_roads').select('road_id')
    if (error || !data) return
    const next = new Set((data as { road_id: string }[]).map((r) => r.road_id))
    set((s) => {
      if (s.hiddenRoadIds.size === next.size && [...next].every((id) => s.hiddenRoadIds.has(id))) {
        return s
      }
      return { hiddenRoadIds: next }
    })
  },

  hideRoadLocally: (roadId) =>
    set((s) => ({
      roads: s.roads.filter((r) => r.id !== roadId),
      hiddenRoadIds: new Set([...s.hiddenRoadIds, roadId]),
      // Deselect the road if it was open — its detail panel must not linger.
      selectedId: s.selectedId === roadId ? null : s.selectedId,
    })),

  // Moderator force-reset: swap one road's rebuilt record straight into the
  // live list (the 5-minute background refresh would otherwise leave the
  // stale record on the map until the next snapshot change).
  replaceRoad: (road) =>
    set((s) => {
      const idx = s.roads.findIndex((r) => r.id === road.id)
      const next = [...s.roads]
      if (idx === -1) next.push(road)
      else next[idx] = road
      return { roads: next }
    }),

  filters: { ...DEFAULT_FILTERS },
  setFilters: (patch) => set((s) => ({ filters: { ...s.filters, ...patch } })),
  resetFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),

  selectedId: null,
  selectedVisitKey: null,
  selectRoad: (id, visitKey) => set({ selectedId: id, selectedVisitKey: id ? (visitKey ?? null) : null }),

  dismissOverlaysSeq: 0,
  dismissOverlays: () =>
    set((s) => ({ selectedId: null, dismissOverlaysSeq: s.dismissOverlaysSeq + 1 })),
  resetForSignOut: () =>
    set((s) => ({
      selectedId: null,
      selectedVisitKey: null,
      routeIds: [],
      routeName: null,
      routeSavedBy: null,
      routeTrims: {},
      trimmingRoadId: null,
      trimDrag: null,
      statsUsername: null,
      ride: null,
      hoveredId: null,
      routeHoverKey: null,
      filters: { ...DEFAULT_FILTERS },
      dismissOverlaysSeq: s.dismissOverlaysSeq + 1,
    })),
  searchDismissSeq: 0,
  dismissSearch: () =>
    set((s) => ({ searchDismissSeq: s.searchDismissSeq + 1 })),

  mapClickSeq: 0,
  bumpMapClick: () => set((s) => ({ mapClickSeq: s.mapClickSeq + 1 })),

  recalculatingId: null,
  setRecalculating: (id) => set({ recalculatingId: id }),

  recalcWatchId: null,
  setRecalcWatchId: (id) => set({ recalcWatchId: id }),
  recalcDrawnId: null,
  setRecalcDrawn: (id) => set({ recalcDrawnId: id }),

  hoveredId: null,
  setHoveredId: (id) => set({ hoveredId: id }),

  routeHoverKey: null,
  setRouteHoverKey: (id) => set({ routeHoverKey: id }),

  routeIds: [],
  routeName: null,
  routeSavedBy: null,
  setRouteIds: (ids) => set({ routeIds: [...ids] }),
  setRouteName: (name) => set({ routeName: name }),
  setRouteSavedBy: (username) => set({ routeSavedBy: username }),
  statsUsername: null,
  openStats: (username) => set({ statsUsername: username }),
  closeStats: () => set({ statsUsername: null }),
  addToRoute: (id) => {
    const s = get()
    const isFirstInstance = !s.routeIds.includes(id)
    // Trim keys are per road INSTANCE (1st, 2nd, 3rd pass…): the first visit
    // keeps the plain road id, later passes get "id#2", "id#3". Counting the
    // instances already in the route gives the new pass its number.
    const pass = s.routeIds.filter((r) => r === id).length + 1
    const trimKey = pass === 1 ? id : `${id}#${pass}`
    set({
      routeIds: [...s.routeIds, id],
      routeName: null,
      routeSavedBy: null,
      ...(isFirstInstance ? {} : { routeTrims: { ...s.routeTrims, [trimKey]: {} } }),
    })
  },
  reorderRoute: (fromIndex, toIndex) =>
    set((s) => {
      if (fromIndex === toIndex) return s
      const next = [...s.routeIds]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return { routeIds: next, routeName: null, routeSavedBy: null }
    }),
  removeFromRoute: (index) =>
    set((s) => {
      const removed = s.routeIds[index]
      if (removed === undefined) return s
      // Trim keys are per occurrence (1st pass = plain id, 2nd = "id#2"…).
      // Removing an instance renumbers the later visits of that road, so
      // their trims shift to the new keys instead of orphaning.
      const nextIds = s.routeIds.filter((_, i) => i !== index)
      // Rebuild the trim map against the new list: each remaining visit of
      // `removed` keeps its own trim (shifted down if an earlier visit went
      // away), every other road's keys are unchanged.
      const nextTrims: RouteTrims = {}
      let seen = 0
      for (let i = 0; i < nextIds.length; i++) {
        const oldIndex = i < index ? i : i + 1
        if (nextIds[i] === removed) {
          const oldKey = trimKeyFor(s.routeIds, oldIndex)
          nextTrims[occurrenceKey(removed, seen)] = s.routeTrims[oldKey] ?? {}
          seen++
        } else {
          const key = trimKeyFor(s.routeIds, oldIndex)
          if (s.routeTrims[key]) nextTrims[key] = s.routeTrims[key]
        }
      }
      return {
        routeIds: nextIds,
        routeName: null,
        routeSavedBy: null,
        routeTrims: nextTrims,
      }
    }),
  clearRoute: () =>
    set({ routeIds: [], routeName: null, routeSavedBy: null, routeTrims: {}, trimmingRoadId: null, trimDrag: null }),
  // Requests the map to frame the WHOLE route. focusSeq increments per call so
  // repeated clicks on the route summary re-frame even if nothing else changed.
  focusRoute: () => set((s) => ({ routeFocusSeq: s.routeFocusSeq + 1 })),
  routeFocusSeq: 0,

  trimmingRoadId: null,
  setTrimmingRoad: (id) => set({ trimmingRoadId: id, trimDrag: null }),
  routeTrims: {},
  // `roadId` is the trim KEY (plain id for a road's first pass, "id#2"… for
  // later visits), so each visit stores its own trim independently.
  setTrim: (roadId, trim) =>
    set((s) => {
      const next = { ...s.routeTrims }
      if (trim) next[roadId] = trim
      else delete next[roadId]
      // A trim changes the ride, so the route's saved name no longer applies.
      return { routeTrims: next, routeName: null, routeSavedBy: null }
    }),
  clearTrim: (roadId) => get().setTrim(roadId, null),
  trimDrag: null,
  setTrimDrag: (d) => set({ trimDrag: d }),

  ride: null,
  startRide: (mode, roadIds) =>
    set({ ride: { mode, roadIds: Array.from(new Set(roadIds)) } }),
  exitRide: () => set({ ride: null }),
}))

// ── Change detection ─────────────────────────────────────
// Stable signature of everything EXCEPT the id (id is immutable).
function roadSignature(r: RoadRecord): string {
  return JSON.stringify([
    r.name,
    r.region,
    r.group,
    r.distance_miles,
    r.fully_paved,
    r.bike_types,
    r.ratings,
    r.description,
    r.google_maps_url,
    r.geometry?.type ?? null,
    r.geometry?.coordinates ?? null,
  ])
}

// ── Filtering ───────────────────────────────────────────────────────────────
const HIGHWAY_RE = /\b(hwy|highway|ca \d|us \d|i-\d|state route|sr \d)/i

export function filterRoads(roads: RoadRecord[], filters: RoadFilters): RoadRecord[] {
  const search = filters.search.trim().toLowerCase()
  return (roads ?? []).filter((road) => {
    if (!road) return false

    if (filters.bikeTypes.length > 0) {
      const types = road.bike_types ?? []
      if (!filters.bikeTypes.some((t) => types.includes(t))) return false
    }

    const r = road.ratings
    const ratingOk = (val: number | null, min: number) => {
      if (min <= 0) return true
      if (val === null || typeof val !== 'number') return false
      return val >= min
    }
    if (!ratingOk(r?.overall ?? null, filters.minOverall)) return false
    if (!ratingOk(r?.fun ?? null, filters.minFun)) return false
    if (!ratingOk(r?.pavement ?? null, filters.minPavement)) return false
    if (!ratingOk(r?.traffic ?? null, filters.minTraffic)) return false
    if (!ratingOk(r?.dirt ?? null, filters.minDirt)) return false

    const len = road.distance_miles
    const lengthOk =
      filters.minLength <= 0 && filters.maxLength >= 1000000
        ? true
        : typeof len === 'number' && len >= filters.minLength && len <= filters.maxLength
    if (!lengthOk) return false

    if (filters.fullyPavedOnly && !road.fully_paved) return false
    if (filters.highwaysOnly && !(typeof road.name === 'string' && HIGHWAY_RE.test(road.name)))
      return false

    const isRated = typeof r?.overall === 'number'
    if (!filters.showRated && isRated) return false
    if (!filters.showUnrated && !isRated) return false

    if (search && !(typeof road.name === 'string' && road.name.toLowerCase().includes(search)))
      return false

    if (filters.region && road.region !== filters.region) return false

    return true
  })
}

export { getRegions }
